import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Globe } from "lucide-react";
import { LANGUAGES } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const links = [
  { to: "/", key: "pub.nav.home", testId: "pub-nav-home" },
  { to: "/events", key: "pub.nav.events", testId: "pub-nav-events" },
  { to: "/partners", key: "pub.nav.partners", testId: "pub-nav-partners" },
  { to: "/about", key: "pub.nav.about", testId: "pub-nav-about" },
];

const LangPicker = ({ compact }) => {
  const { t, i18n } = useTranslation();
  const { setLanguage } = useAuth();
  return (
    <div className={`flex items-center gap-1 ${compact ? "" : "rounded-full bg-muted p-1"}`}
      role="group" aria-label={t("common.language")} data-testid="pub-lang-switcher">
      {!compact && <Globe className="ml-2 h-4 w-4 text-muted-foreground" />}
      {LANGUAGES.map((l) => (
        <button key={l.code} data-testid={`pub-lang-${l.code}`} onClick={() => setLanguage(l.code)}
          aria-current={i18n.language === l.code}
          className={`h-9 rounded-full px-3 text-xs font-bold transition-colors ${
            i18n.language === l.code ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"
          }`}>
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const PublicHeader = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur"
      data-testid="public-header">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-4 sm:px-6">
        <Link to="/" data-testid="pub-brand" className="font-display text-xl font-extrabold tracking-tight">
          Vytali<span className="text-accent">Days</span>
        </Link>
        <nav className="ml-6 hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={l.testId}
              className={({ isActive }) =>
                `h-10 rounded-full px-4 text-sm font-semibold leading-10 transition-colors ${
                  isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}>
              {t(l.key)}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto hidden items-center gap-3 lg:flex">
          <LangPicker />
          <Button asChild className="h-11 rounded-full px-5 font-bold">
            <Link to="/login" data-testid="pub-login-btn">{t("pub.nav.login")}</Link>
          </Button>
        </div>
        <button className="ml-auto lg:hidden" onClick={() => setOpen(!open)}
          aria-label="menu" data-testid="pub-menu-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-border bg-background px-4 pb-5 pt-3 lg:hidden" data-testid="pub-mobile-menu">
          <nav className="flex flex-col">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} onClick={() => setOpen(false)}
                data-testid={`${l.testId}-mobile`}
                className="flex h-14 items-center border-b border-border text-base font-semibold">
                {t(l.key)}
              </NavLink>
            ))}
          </nav>
          <div className="mt-4 flex items-center justify-between gap-3">
            <LangPicker compact />
            <Button asChild className="h-12 flex-1 rounded-full font-bold">
              <Link to="/login" data-testid="pub-login-btn-mobile" onClick={() => setOpen(false)}>
                {t("pub.nav.login")}
              </Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
